import React from "react";
import "./CrosswordGame.css";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/*
 * The drawings of the Baker Street Math Crossword (ADR 0134 §7), all inline SVG and CSS:
 * a detective in a deerstalker and cape, drawn here as a plain dark silhouette, and the
 * brass plate from the door of 221B. Both are original simple figures; nothing is traced
 * from any film, television or game, and the detective holds no pipe (this is a children's
 * page). They are decoration, so they are hidden from a screen reader.
 */

/** A detective in a deerstalker and a caped coat, holding up a magnifying glass. */
export function Silhouette() {
    return (
        <svg className="cwFigure" viewBox="0 0 80 100" aria-hidden="true" focusable="false">
            {/* The cape: shoulders, a short second layer over them, and a collar. */}
            <path d="M6 100 L12 66 Q16 52 40 50 Q64 52 68 66 L74 100 Z" fill="#1f2a24" />
            <path d="M12 66 Q40 76 68 66 L70 78 Q40 88 10 78 Z" fill="#33423a" />
            <path d="M30 50 L40 62 L50 50 Q40 46 30 50 Z" fill="#33423a" />
            {/* Neck and face. */}
            <rect x="34" y="42" width="12" height="12" rx="4" fill="#1f2a24" />
            <ellipse cx="40" cy="33" rx="13" ry="15" fill="#1f2a24" />
            {/* The deerstalker: a dome, a peak front and back, ear flaps tied on top. */}
            <path d="M24 30 Q24 8 40 8 Q56 8 56 30 Z" fill="#33423a" />
            <path d="M24 30 L9 34 L12 28 L26 22 Z" fill="#1f2a24" />
            <path d="M56 30 L71 34 L68 28 L54 22 Z" fill="#1f2a24" />
            <path d="M33 9 Q40 2 47 9 Q40 6 33 9 Z" fill="#1f2a24" />
            <path d="M25 30 L30 24 L50 24 L55 30" fill="none" stroke="#1f2a24" strokeWidth="2" />
            {/* The magnifying glass: a brass ring, a pale lens and a short handle. */}
            <line x1="57" y1="82" x2="66" y2="94" stroke="#8a5a2b" strokeWidth="4" strokeLinecap="round" />
            <circle cx="52" cy="72" r="12" fill="#e8f1ee" fillOpacity="0.55" stroke="#b8860b" strokeWidth="3.5" />
            <path d="M45 70 Q47 64 53 63" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );
}

/** The brass plate: 221B, over Baker Street. */
export function Plate() {
    const t = useTranslation();
    return (
        <div className="cwPlate" aria-hidden="true">
            <strong>221B</strong>
            <span>{t("flashCards.crosswordPlateStreet")}</span>
        </div>
    );
}

/** A magnifying glass for the glasses a case is worth: full while it is still worth that much, faded once lost. */
export function Lens({ lost }: { lost: boolean }) {
    return (
        <svg className={`cwLens${lost ? " cwLens--lost" : ""}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="10" cy="10" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.6" />
            <line x1="15" y1="15" x2="21.5" y2="21.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
    );
}
